import React from "react";
import { useI18n } from "../../i18n";
import { Check } from "lucide-react";

const tiers = (t) => [
  {
    id: "starter",
    name: t("pricing.starter_t"),
    description: t("pricing.starter_d"),
    price: 25,
    days: 14,
    highlighted: false,
    features: [
      "Single-page branded website",
      "Mobile-optimised design",
      "Live menu display",
      "Custom domain included",
      "Email support",
    ],
  },
  {
    id: "growth",
    name: t("pricing.growth_t"),
    description: t("pricing.growth_d"),
    price: 125,
    days: 21,
    highlighted: true,
    features: [
      "Full multi-page website",
      "Online reservation system (0% commission)",
      "Google Maps & SEO setup",
      "Guest analytics dashboard",
      "Social media integration",
      "GDPR compliance toolkit",
      "Multi-language website",
      "Priority 24-hour support",
    ],
  },
  {
    id: "pro",
    name: t("pricing.pro_t"),
    description: t("pricing.pro_d"),
    price: 249,
    days: 30,
    highlighted: false,
    features: [
      "Everything in Growth",
      "Multi-location support",
      "Odoo POS & delivery integrations (Square, Lightspeed, Uber Eats)",
      "Custom brand identity kit",
      "Odoo email marketing tools set-up",
      "Advanced GA4 analytics",
      "Dedicated account manager",
    ],
  },
];

export default function Pricing() {
  const { t } = useI18n();
  const list = tiers(t);

  return (
    <section id="pricing" className="section-y" data-testid="pricing-section">
      <div className="container-x">
        <div className="max-w-3xl">
          <p className="eyebrow reveal">{t("pricing.eyebrow")}</p>
          <h2 className="display-h2 mt-4 text-brand-ink reveal" data-delay="1" data-testid="pricing-title">
            {t("pricing.title")}
          </h2>
          <p className="body-lg mt-5 reveal" data-delay="2">{t("pricing.lead")}</p>
        </div>

        <div className="mt-14 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 items-stretch">
          {list.map((tier, idx) => {
            const isHi = tier.highlighted;
            return (
              <article
                key={tier.id}
                data-testid={`pricing-${tier.id}-card`}
                className={`reveal relative rounded-2xl p-8 md:p-10 flex flex-col h-full transition-all duration-300 ${
                  isHi
                    ? "bg-brand-forest text-brand-ivory border border-brand-forest shadow-[0_40px_80px_-40px_rgba(26,55,45,0.55)] md:-translate-y-3"
                    : "bg-white border border-brand-line hover:-translate-y-1 hover:shadow-[0_24px_60px_-32px_rgba(26,55,45,0.25)]"
                }`}
                data-delay={idx + 1}
              >
                {isHi && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center px-3 py-1 rounded-full bg-brand-terracotta text-white text-[11px] uppercase tracking-[0.2em]"
                    data-testid="pricing-most-popular"
                  >
                    {t("pricing.most_popular")}
                  </span>
                )}
                <div className="flex items-baseline gap-2">
                  <h3 className={`font-serif text-3xl md:text-4xl tracking-tight ${isHi ? "text-brand-ivory" : "text-brand-ink"}`}>
                    {tier.name}
                  </h3>
                </div>
                <p className={`mt-2 text-sm leading-relaxed ${isHi ? "text-brand-ivory/75" : "text-brand-mute"}`}>
                  {tier.description}
                </p>

                <div className="mt-7 flex items-baseline gap-1">
                  <span className={`font-serif text-5xl md:text-6xl tracking-tight ${isHi ? "text-brand-ivory" : "text-brand-ink"}`}>
                    €{tier.price}
                  </span>
                  <span className={`text-sm ${isHi ? "text-brand-ivory/70" : "text-brand-mute"}`}>
                    {t("pricing.per_month")}
                  </span>
                </div>
                <p className={`mt-1 text-xs uppercase tracking-[0.16em] ${isHi ? "text-brand-terracotta" : "text-brand-terracotta"}`}>
                  {t("pricing.launch_in")} {tier.days} {t("pricing.days")}
                </p>

                <ul className={`mt-7 space-y-3 ${isHi ? "text-brand-ivory/90" : "text-brand-ink"}`}>
                  {tier.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm leading-relaxed">
                      <Check size={16} strokeWidth={2} className={isHi ? "text-brand-terracotta mt-0.5 shrink-0" : "text-brand-forest mt-0.5 shrink-0"} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#lead"
                  data-testid={`pricing-${tier.id}-cta`}
                  className={`mt-8 inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-medium transition-all duration-300 ${
                    isHi
                      ? "bg-brand-terracotta text-white hover:bg-brand-terracotta-hover hover:-translate-y-[1px]"
                      : "bg-brand-forest text-brand-ivory hover:bg-brand-forest-hover hover:-translate-y-[1px]"
                  }`}
                >
                  {t("pricing.cta")}
                </a>
              </article>
            );
          })}
        </div>

        <p className="mt-10 text-center text-sm text-brand-mute reveal">
          €0 setup · Cancel any time · GDPR compliant · Hosted in the EU
        </p>
      </div>
    </section>
  );
}
